import '../../styles/components/admin.css'
import { requireAuth, getCustomers, getCustomer, getOrdersByEmail, updateCustomerNotes, getAccessToken, getAllProducts } from '../../lib/supabase.js'
import { formatPrice, showToast } from '../../utils/helpers.js'

export function renderAdminUsers(outlet) {
  requireAuth(async () => {
    outlet.innerHTML = `
      <div class="admin-page">
        <div class="container">
          <div class="admin-topbar" style="margin-bottom: var(--space-6);">
            <h1 class="admin-title">Customers</h1>
            <a href="#/admin" class="admin-back">
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2" width="16" height="16">
                <path stroke-linecap="round" stroke-linejoin="round" d="M15.75 19.5L8.25 12l7.5-7.5"/>
              </svg>
              Back to Products
            </a>
          </div>
          <div id="users-error" class="admin-error" style="display:none; margin-bottom: var(--space-4);"></div>
          <div id="users-content">
            <div class="products-loading" style="padding: var(--space-16) 0; text-align:center;">
              <div class="spinner"></div>
            </div>
          </div>
        </div>
      </div>
    `
    await loadCustomers()
  })
}

async function loadCustomers() {
  const content = document.getElementById('users-content')
  if (!content) return

  try {
    const customers = await getCustomers()

    if (customers.length === 0) {
      content.innerHTML = `<p style="color: var(--color-text-secondary); text-align:center; padding: var(--space-8);">No customers yet. Customers are created automatically when orders are placed.</p>`
      return
    }

    content.innerHTML = `
      <div class="admin-table-wrap">
        <table class="admin-table">
          <thead>
            <tr>
              <th>Customer</th>
              <th>Email</th>
              <th>Phone</th>
              <th>Account</th>
              <th>Joined</th>
              <th style="width:100px;">Actions</th>
            </tr>
          </thead>
          <tbody>
            ${customers.map(c => `
              <tr>
                <td class="cell-name">${esc(c.name || '—')}</td>
                <td>${esc(c.email)}</td>
                <td>${esc(c.phone || '—')}</td>
                <td>
                  <span class="admin-badge ${c.auth_id ? 'admin-badge-active' : 'admin-badge-inactive'}">
                    ${c.auth_id ? 'Yes' : 'No'}
                  </span>
                </td>
                <td>${new Date(c.created_at).toLocaleDateString()}</td>
                <td class="cell-actions">
                  <button class="btn btn-sm" style="font-size:var(--text-xs);"
                    data-view="${c.id}" data-email="${esc(c.email)}" data-name="${esc(c.name || '')}">View</button>
                </td>
              </tr>
            `).join('')}
          </tbody>
        </table>
      </div>
      <div id="customer-detail" style="margin-top: var(--space-6);"></div>
    `

    content.querySelectorAll('[data-view]').forEach(btn => {
      btn.addEventListener('click', () => {
        showCustomerDetail(btn.dataset.view, btn.dataset.email, btn.dataset.name)
      })
    })

  } catch (err) {
    content.innerHTML = `<div class="admin-error">${esc(err.message)}</div>`
  }
}

async function showCustomerDetail(id, email, name) {
  const detail = document.getElementById('customer-detail')
  if (!detail) return

  detail.innerHTML = `
    <div class="card" style="padding: var(--space-6);">
      <div style="display:flex; justify-content:space-between; align-items:center; margin-bottom: var(--space-5);">
        <h2 style="font-size: var(--text-lg); font-weight: var(--font-semibold);">${esc(name || email)}</h2>
        <button class="btn btn-sm" id="close-detail" style="font-size:var(--text-xs);">Close</button>
      </div>
      <div style="display:flex; gap: var(--space-8);">
        <div style="flex:1;">
          <h3 style="font-size: var(--text-sm); font-weight: var(--font-semibold); margin-bottom: var(--space-3); color: var(--color-text-secondary);">Orders</h3>
          <div id="customer-orders">
            <div class="spinner" style="width:16px;height:16px;border-width:2px;"></div>
          </div>
        </div>
        <div style="flex:1;">
          <h3 style="font-size: var(--text-sm); font-weight: var(--font-semibold); margin-bottom: var(--space-3); color: var(--color-text-secondary);">Notes</h3>
          <div id="customer-notes-wrap">
            <div class="spinner" style="width:16px;height:16px;border-width:2px;"></div>
          </div>
        </div>
      </div>

      <div style="margin-top: var(--space-8); padding-top: var(--space-6); border-top: 1px solid var(--color-border);">
        <h3 style="font-size: var(--text-sm); font-weight: var(--font-semibold); margin-bottom: var(--space-3); color: var(--color-text-secondary);">Billing — invoices &amp; payment links</h3>
        <div id="customer-billing">
          <div class="spinner" style="width:16px;height:16px;border-width:2px;"></div>
        </div>
      </div>
    </div>
  `

  detail.scrollIntoView({ behavior: 'smooth', block: 'nearest' })

  document.getElementById('close-detail').addEventListener('click', () => {
    detail.innerHTML = ''
  })

  // Load orders and notes in parallel
  const [orders, customer] = await Promise.all([
    getOrdersByEmail(email).catch(() => []),
    getCustomer(id).catch(() => null),
  ])

  // Render orders
  const ordersEl = document.getElementById('customer-orders')
  if (ordersEl) {
    if (orders.length === 0) {
      ordersEl.innerHTML = '<p class="text-sm text-secondary">No orders yet.</p>'
    } else {
      const totalSpent = orders.reduce((sum, o) => sum + (o.total_cents || 0), 0)
      ordersEl.innerHTML = `
        <p class="text-sm" style="margin-bottom: var(--space-3);">
          <strong>${orders.length}</strong> order${orders.length !== 1 ? 's' : ''} ·
          <strong>${formatPrice(totalSpent)}</strong> total
        </p>
        ${orders.map(o => `
          <div style="padding: var(--space-3) 0; border-bottom: 1px solid var(--color-border); font-size: var(--text-sm);">
            <div style="display:flex; justify-content:space-between; align-items:flex-start;">
              <div>
                <div class="font-semibold">${new Date(o.created_at).toLocaleDateString()}</div>
                <div class="text-secondary" style="font-size: var(--text-xs);">
                  ${o.stripe_session_id?.startsWith('in_') ? 'Invoice' : 'Checkout'} · ${esc(o.stripe_session_id?.slice(0, 20))}…
                  ${o.stripe_payment_intent_id ? ` · <a href="https://dashboard.stripe.com/payments/${esc(o.stripe_payment_intent_id)}" target="_blank" rel="noopener">Stripe ↗</a>` : ''}
                </div>
              </div>
              <div style="text-align:right;">
                <div class="font-semibold">${formatPrice(o.total_cents)}</div>
                <span class="admin-badge ${o.status === 'paid' ? 'admin-badge-active' : 'admin-badge-inactive'}" style="font-size:10px;">${o.status}</span>
              </div>
            </div>
            <div class="order-breakdown" data-order-breakdown="${esc(o.stripe_session_id)}" style="margin-top: var(--space-2); font-size: var(--text-xs); color: var(--color-text-secondary);">
              Loading items…
            </div>
          </div>
        `).join('')}
      `
    }
  }

  // Render notes
  const notesEl = document.getElementById('customer-notes-wrap')
  if (notesEl) {
    const notes = customer?.notes || ''
    notesEl.innerHTML = `
      <textarea id="customer-notes" class="form-input form-textarea" rows="5"
        placeholder="Add internal notes about this customer…"
        style="font-size: var(--text-sm);">${esc(notes)}</textarea>
      <button class="btn btn-sm btn-primary" id="save-notes-btn" style="margin-top: var(--space-2); font-size:var(--text-xs);">Save Notes</button>
      <span id="notes-saved" style="display:none; font-size:var(--text-xs); color:var(--color-success); margin-left: var(--space-2);">Saved</span>
    `
    document.getElementById('save-notes-btn').addEventListener('click', async () => {
      const btn = document.getElementById('save-notes-btn')
      const val = document.getElementById('customer-notes').value
      btn.disabled = true
      btn.textContent = 'Saving…'
      try {
        await updateCustomerNotes(id, val)
        document.getElementById('notes-saved').style.display = 'inline'
        setTimeout(() => {
          const el = document.getElementById('notes-saved')
          if (el) el.style.display = 'none'
        }, 2000)
      } catch (err) {
        alert('Failed to save: ' + (err.message || 'Unknown error'))
      } finally {
        btn.disabled = false
        btn.textContent = 'Save Notes'
      }
    })
  }

  // Fill in per-order line-item breakdowns from Stripe
  if (orders.length) loadOrderBreakdowns(email, orders.map(o => o.stripe_session_id).filter(Boolean))

  // Render billing (Stripe invoices + payment links)
  renderBilling(email, name)
}

async function loadOrderBreakdowns(email, ids) {
  let details = []
  try {
    const data = await billingRequest({ action: 'order_details', email, ids })
    details = data.orders || []
  } catch (err) {
    document.querySelectorAll('[data-order-breakdown]').forEach(el => {
      el.innerHTML = `<span style="color: var(--color-error);">Could not load items: ${esc(err.message)}</span>`
    })
    return
  }

  for (const d of details) {
    const el = document.querySelector(`[data-order-breakdown="${CSS.escape(d.id)}"]`)
    if (!el) continue
    if (d.error) {
      el.innerHTML = `<span style="color: var(--color-error);">${esc(d.error)}</span>`
      continue
    }
    const row = (left, right, opts = {}) => `
      <div style="display:flex; justify-content:space-between; gap: var(--space-3); padding: 1px 0; ${opts.strong ? 'color: var(--color-text); font-weight: var(--font-semibold); border-top: 1px solid var(--color-border); margin-top: 3px; padding-top: 3px;' : ''}">
        <span>${left}</span><span style="white-space:nowrap;">${right}</span>
      </div>`
    el.innerHTML =
      d.items.map(i => row(
        `<strong style="color: var(--color-text);">${i.quantity}×</strong> ${esc(i.description)} <span class="text-muted">@ ${formatPrice(i.unit_cents)}</span>`,
        formatPrice(i.amount_cents)
      )).join('') +
      (d.discount_cents > 0 ? row(
        `Discount${d.discounts?.[0]?.code ? ` (${esc(d.discounts.map(x => x.code).filter(Boolean).join(', '))})` : ''}`,
        `−${formatPrice(d.discount_cents)}`
      ) : '') +
      (d.shipping_cents > 0 ? row(`Shipping${d.shipping_label ? ` — ${esc(d.shipping_label)}` : ''}`, formatPrice(d.shipping_cents)) : '') +
      (d.tax_cents > 0 ? row('Tax', formatPrice(d.tax_cents)) : '') +
      row('Total', formatPrice(d.total_cents), { strong: true })
  }
}

// ============================================================
// Billing — ad-hoc invoices & payment links (Stripe)
// ============================================================

async function billingRequest(payload) {
  const token = await getAccessToken()
  if (!token) throw new Error('Not signed in')
  const res = await fetch('/api/admin/customer-billing', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(payload),
  })
  const data = await res.json().catch(() => ({}))
  if (!res.ok) throw new Error(data?.error || `Request failed (${res.status})`)
  return data
}

const MIN_TOTAL_CENTS = 50

function renderBilling(email, name) {
  const wrap = document.getElementById('customer-billing')
  if (!wrap) return

  wrap.innerHTML = `
    <div id="billing-error" class="admin-error" style="display:none; margin-bottom: var(--space-4);"></div>
    <div id="billing-success" class="admin-success" style="display:none; margin-bottom: var(--space-4);"></div>

    <form id="billing-form" novalidate style="margin-bottom: var(--space-6);">
      <div class="admin-table-wrap" style="margin-bottom: var(--space-3);">
        <table class="admin-table billing-lines">
          <colgroup>
            <col />
            <col style="width:120px;" />
            <col style="width:150px;" />
            <col style="width:120px;" />
            <col style="width:56px;" />
          </colgroup>
          <thead>
            <tr>
              <th>Item</th>
              <th>Qty</th>
              <th>Unit price</th>
              <th style="text-align:right;">Total</th>
              <th></th>
            </tr>
          </thead>
          <tbody id="billing-lines-body"></tbody>
        </table>
      </div>
      <div style="display:flex; gap: var(--space-2); margin-bottom: var(--space-5); flex-wrap:wrap;">
        <button type="button" class="btn btn-sm" id="billing-add-product">+ Add product</button>
        <button type="button" class="btn btn-sm" id="billing-add-custom">+ Add custom line</button>
      </div>

      <div class="admin-form-row" style="grid-template-columns: 1fr 1fr 1fr 1fr; align-items:end;">
        <div class="form-group">
          <label class="form-label" for="billing-shipping">Shipping (USD)</label>
          <input id="billing-shipping" class="form-input" type="number" min="0" step="0.01" placeholder="0.00" />
        </div>
        <div class="form-group">
          <label class="form-label" for="billing-discount-type">Discount</label>
          <select id="billing-discount-type" class="form-input">
            <option value="none">None</option>
            <option value="percent">Percent off (%)</option>
            <option value="amount">Amount off ($)</option>
          </select>
        </div>
        <div class="form-group">
          <label class="form-label" for="billing-discount-value">Discount value</label>
          <input id="billing-discount-value" class="form-input" type="number" min="0" step="0.01" placeholder="0" disabled />
        </div>
        <div class="form-group">
          <label class="form-label" for="billing-due">Invoice due in (days)</label>
          <input id="billing-due" class="form-input" type="number" min="1" max="365" step="1" value="30" />
        </div>
      </div>

      <div class="form-group">
        <label class="form-label" for="billing-memo">Memo (optional, shown on the invoice)</label>
        <input id="billing-memo" class="form-input" type="text" maxlength="500" placeholder="e.g. Per our email — custom radius platen" />
      </div>

      <div id="billing-totals" style="max-width: 320px; margin-left:auto; font-size: var(--text-sm); margin-bottom: var(--space-4);"></div>

      <div style="display:flex; gap: var(--space-3); flex-wrap:wrap;">
        <button type="submit" class="btn btn-primary btn-sm" id="billing-create-invoice" data-kind="invoice">Preview invoice</button>
        <button type="submit" class="btn btn-primary btn-sm" id="billing-create-link" data-kind="payment_link">Create payment link</button>
      </div>
      <p class="text-secondary" style="font-size: var(--text-xs); margin-top: var(--space-2);">
        Preview invoice: review the invoice first, then send it (Stripe emails it with a pay link) or create it without sending.
        Payment link: shareable URL, no email sent — if a discount is set, the link shows a single net-priced line.
      </p>
    </form>

    <div id="billing-lists">
      <div class="spinner" style="width:16px;height:16px;border-width:2px;"></div>
    </div>
  `

  // ----- Line-item builder state -----
  let products = []
  let lines = [] // { product_id, description, unit_cents, quantity, custom }

  const body = document.getElementById('billing-lines-body')
  const totalsEl = document.getElementById('billing-totals')
  const discountTypeEl = document.getElementById('billing-discount-type')
  const discountValueEl = document.getElementById('billing-discount-value')
  const shippingEl = document.getElementById('billing-shipping')

  const productLabel = p => `${p.name}${p.sku ? ` (${p.sku})` : ''}${p.active === false ? ' — inactive' : ''}`

  const productOptions = selectedId => products.map(p =>
    `<option value="${esc(p.id)}" ${p.id === selectedId ? 'selected' : ''}>${esc(productLabel(p))}</option>`
  ).join('')

  function renderLines() {
    if (lines.length === 0) {
      body.innerHTML = `<tr><td colspan="5" class="text-secondary" style="text-align:center; padding: var(--space-4);">No items yet — add a product or a custom line.</td></tr>`
    } else {
      body.innerHTML = lines.map((l, i) => `
        <tr data-i="${i}">
          <td>
            ${l.custom
              ? `<input class="form-input" data-field="description" type="text" maxlength="250" placeholder="Description" value="${esc(l.description)}" style="width:100%;" />`
              : `<select class="form-input" data-field="product_id" style="width:100%;">${productOptions(l.product_id)}</select>`}
          </td>
          <td><input class="form-input" data-field="quantity" type="number" min="1" max="999" step="1" value="${l.quantity}" style="width:100%;" /></td>
          <td><input class="form-input" data-field="unit" type="number" min="0" step="0.01" value="${(l.unit_cents / 100).toFixed(2)}" style="width:100%;" /></td>
          <td style="text-align:right; white-space:nowrap;" class="font-semibold">${formatPrice(l.unit_cents * l.quantity)}</td>
          <td><button type="button" class="btn btn-sm" data-remove="${i}" title="Remove" style="border-color: var(--color-error); color: var(--color-error);">✕</button></td>
        </tr>
      `).join('')
    }
    renderTotals()
  }

  function computeTotals() {
    const subtotal = lines.reduce((sum, l) => sum + l.unit_cents * l.quantity, 0)
    const shipping = Math.max(0, Math.round((parseFloat(shippingEl.value) || 0) * 100))
    const dType = discountTypeEl.value
    const dValRaw = parseFloat(discountValueEl.value) || 0
    let discount = 0
    let discountLabel = ''
    if (dType === 'percent' && dValRaw > 0) {
      discount = Math.round(subtotal * Math.min(dValRaw, 100) / 100)
      discountLabel = `Discount (${dValRaw}%)`
    } else if (dType === 'amount' && dValRaw > 0) {
      discount = Math.min(Math.round(dValRaw * 100), subtotal)
      discountLabel = 'Discount'
    }
    return { subtotal, shipping, discount, discountLabel, total: subtotal - discount + shipping }
  }

  /** Per-line effective unit cost after the discount (amount discounts are prorated by line subtotal). */
  function discountedUnits(t) {
    if (t.discount <= 0 || t.subtotal <= 0) return []
    return lines.map(l => {
      const lineSub = l.unit_cents * l.quantity
      const share = Math.round(t.discount * lineSub / t.subtotal)
      const net = lineSub - share
      return { line: l, net_cents: net, unit: net / l.quantity } // unit may be fractional cents
    })
  }

  function renderTotals() {
    const t = computeTotals()
    const row = (label, val, strong) => `
      <div style="display:flex; justify-content:space-between; gap: var(--space-3); padding: 2px 0; ${strong ? 'border-top:1px solid var(--color-border); margin-top:4px; padding-top:6px; font-weight: var(--font-semibold);' : ''}">
        <span class="${strong ? '' : 'text-secondary'}">${label}</span><span style="white-space:nowrap;">${val}</span>
      </div>`
    const units = discountedUnits(t)
    totalsEl.innerHTML =
      row('Subtotal', formatPrice(t.subtotal)) +
      (t.discount > 0 ? row(t.discountLabel, `−${formatPrice(t.discount)}`) : '') +
      (units.length ? `
        <div style="font-size: var(--text-xs); color: var(--color-text-secondary); padding: 2px 0 4px var(--space-3); border-left: 2px solid var(--color-border); margin: 2px 0 4px;">
          ${units.map(u => `
            <div style="display:flex; justify-content:space-between; gap: var(--space-3);">
              <span>${esc(u.line.description || 'Item')} — <strong style="color: var(--color-text);">${fmtUnit(u.unit)}</strong>/ea after discount</span>
              <span style="white-space:nowrap;">${u.line.quantity} × ${fmtUnit(u.unit)} = ${formatPrice(u.net_cents)}</span>
            </div>`).join('')}
        </div>` : '') +
      (t.shipping > 0 ? row('Shipping', formatPrice(t.shipping)) : '') +
      row('Total', formatPrice(t.total), true)
  }

  function addProductLine() {
    if (products.length === 0) return showBillingError('No products in the catalog — add a custom line instead.')
    const p = products.find(x => x.active !== false) || products[0]
    lines.push({ product_id: p.id, description: p.name, unit_cents: p.price_cents, quantity: 1, custom: false })
    renderLines()
  }

  function addCustomLine() {
    lines.push({ product_id: null, description: '', unit_cents: 0, quantity: 1, custom: true })
    renderLines()
    const last = body.querySelector('tr:last-child [data-field="description"]')
    if (last) last.focus()
  }

  document.getElementById('billing-add-product').addEventListener('click', addProductLine)
  document.getElementById('billing-add-custom').addEventListener('click', addCustomLine)

  body.addEventListener('input', e => {
    const tr = e.target.closest('tr[data-i]')
    if (!tr) return
    const l = lines[Number(tr.dataset.i)]
    const field = e.target.dataset.field
    if (field === 'description') l.description = e.target.value
    if (field === 'quantity') l.quantity = Math.max(1, Math.min(999, parseInt(e.target.value, 10) || 1))
    if (field === 'unit') l.unit_cents = Math.max(0, Math.round((parseFloat(e.target.value) || 0) * 100))
    if (field !== 'product_id') {
      // Update the line total cell without re-rendering the row (keeps focus)
      tr.children[3].textContent = formatPrice(l.unit_cents * l.quantity)
      renderTotals()
    }
  })

  body.addEventListener('change', e => {
    const tr = e.target.closest('tr[data-i]')
    if (!tr) return
    const l = lines[Number(tr.dataset.i)]
    if (e.target.dataset.field === 'product_id') {
      const p = products.find(x => x.id === e.target.value)
      if (p) {
        l.product_id = p.id
        l.description = p.name
        l.unit_cents = p.price_cents
      }
      renderLines()
    }
  })

  body.addEventListener('click', e => {
    const btn = e.target.closest('[data-remove]')
    if (!btn) return
    lines.splice(Number(btn.dataset.remove), 1)
    renderLines()
  })

  discountTypeEl.addEventListener('change', () => {
    discountValueEl.disabled = discountTypeEl.value === 'none'
    if (discountTypeEl.value === 'none') discountValueEl.value = ''
    renderTotals()
  })
  discountValueEl.addEventListener('input', renderTotals)
  shippingEl.addEventListener('input', renderTotals)

  // Track which submit button was pressed (invoice vs payment link)
  let submitKind = 'invoice'
  document.querySelectorAll('#billing-form [data-kind]').forEach(btn => {
    btn.addEventListener('click', () => { submitKind = btn.dataset.kind })
  })

  function buildOrderPayload() {
    const t = computeTotals()
    const dType = discountTypeEl.value
    const dVal = parseFloat(discountValueEl.value) || 0
    return {
      t,
      payload: {
        lines: lines.map(l => ({
          description: l.description.trim(),
          unit_cents: l.unit_cents,
          quantity: l.quantity,
          product_id: l.product_id || undefined,
        })),
        shipping_cents: t.shipping,
        discount: dType !== 'none' && dVal > 0
          ? { type: dType, value: dType === 'percent' ? dVal : Math.round(dVal * 100) }
          : undefined,
      },
    }
  }

  function setBusy(kind, busy) {
    const buttons = [...document.querySelectorAll('#billing-form [data-kind]')]
    buttons.forEach(b => { b.disabled = busy })
    const btn = buttons.find(b => b.dataset.kind === kind)
    if (btn) {
      if (busy) { btn.dataset.label = btn.textContent; btn.textContent = 'Creating…' }
      else if (btn.dataset.label) { btn.textContent = btn.dataset.label }
    }
  }

  function resetBuilder(form) {
    lines = []
    form.reset()
    discountValueEl.disabled = true
    renderLines()
  }

  async function createInvoice(form, sendEmail) {
    const { t, payload } = buildOrderPayload()
    const daysUntilDue = parseInt(document.getElementById('billing-due').value, 10) || 30
    const memo = document.getElementById('billing-memo').value.trim()
    setBusy('invoice', true)
    try {
      const data = await billingRequest({
        action: 'create_invoice', email, name, ...payload,
        memo, days_until_due: daysUntilDue, send_email: sendEmail,
      })
      const url = data.invoice?.hosted_invoice_url
      showBillingSuccess(
        `Invoice ${esc(data.invoice?.number || data.invoice?.id)} for ${formatPrice(data.invoice?.total_cents || t.total)} created${data.sent ? ' and emailed to ' + esc(email) : ' (not sent — use Email in the list below when ready)'}.` +
        (url ? ` <a href="${esc(url)}" target="_blank" rel="noopener">Open</a> · <button type="button" class="btn-link" data-copy="${esc(url)}">Copy link</button>` : '')
      )
      resetBuilder(form)
      await loadBillingLists(email)
    } catch (err) {
      showBillingError(err.message || 'Failed to create invoice.')
    } finally {
      setBusy('invoice', false)
    }
  }

  async function createPaymentLink(form) {
    const { t, payload } = buildOrderPayload()
    setBusy('payment_link', true)
    try {
      const data = await billingRequest({ action: 'create_payment_link', email, name, ...payload })
      const url = data.payment_link?.url
      showBillingSuccess(
        `Payment link for ${formatPrice(t.total)} created.` +
        (url ? ` <a href="${esc(url)}" target="_blank" rel="noopener">Open</a> · <button type="button" class="btn-link" data-copy="${esc(url)}">Copy link</button>` : '')
      )
      if (url) copyToClipboard(url, 'Payment link copied to clipboard')
      resetBuilder(form)
      await loadBillingLists(email)
    } catch (err) {
      showBillingError(err.message || 'Failed to create payment link.')
    } finally {
      setBusy('payment_link', false)
    }
  }

  function showInvoicePreview(form) {
    const t = computeTotals()
    const daysUntilDue = parseInt(document.getElementById('billing-due').value, 10) || 30
    const memo = document.getElementById('billing-memo').value.trim()
    const issued = new Date()
    const due = new Date(issued.getTime() + daysUntilDue * 86400000)
    const units = discountedUnits(t)

    const existing = document.getElementById('invoice-preview')
    if (existing) existing.remove()

    const modal = document.createElement('div')
    modal.id = 'invoice-preview'
    modal.className = 'invoice-preview-overlay'
    modal.innerHTML = `
      <div class="invoice-preview" role="dialog" aria-modal="true" aria-labelledby="invoice-preview-title">
        <div class="invoice-preview-head">
          <div>
            <div id="invoice-preview-title" class="invoice-preview-brand">Airplaten</div>
            <div class="text-secondary" style="font-size: var(--text-xs);">Invoice preview — not created yet</div>
          </div>
          <button type="button" class="btn btn-sm" id="invoice-preview-close">✕</button>
        </div>

        <div class="invoice-preview-meta">
          <div>
            <div class="invoice-preview-label">Bill to</div>
            <div>${esc(name || '—')}</div>
            <div class="text-secondary">${esc(email)}</div>
          </div>
          <div style="text-align:right;">
            <div><span class="invoice-preview-label">Date of issue</span> ${issued.toLocaleDateString()}</div>
            <div><span class="invoice-preview-label">Due</span> ${due.toLocaleDateString()} (${daysUntilDue} days)</div>
            <div><span class="invoice-preview-label">Currency</span> USD</div>
          </div>
        </div>

        <table class="invoice-preview-table">
          <thead><tr><th>Description</th><th style="text-align:right;">Qty</th><th style="text-align:right;">Unit price</th><th style="text-align:right;">Amount</th></tr></thead>
          <tbody>
            ${lines.map(l => `
              <tr>
                <td>${esc(l.description)}</td>
                <td style="text-align:right;">${l.quantity}</td>
                <td style="text-align:right;">${formatPrice(l.unit_cents)}</td>
                <td style="text-align:right;">${formatPrice(l.unit_cents * l.quantity)}</td>
              </tr>`).join('')}
            ${t.discount > 0 ? `
              <tr>
                <td colspan="3">${esc(t.discountLabel)}${units.length ? `<div class="text-secondary" style="font-size: var(--text-xs);">${units.map(u => `${esc(u.line.description)}: ${fmtUnit(u.unit)}/ea after discount`).join(' · ')}</div>` : ''}</td>
                <td style="text-align:right;">−${formatPrice(t.discount)}</td>
              </tr>` : ''}
          </tbody>
        </table>

        <div class="invoice-preview-totals">
          <div><span>Subtotal</span><span>${formatPrice(t.subtotal)}</span></div>
          ${t.discount > 0 ? `<div><span>Discount</span><span>−${formatPrice(t.discount)}</span></div>` : ''}
          ${t.shipping > 0 ? `<div><span>Shipping</span><span>${formatPrice(t.shipping)}</span></div>` : ''}
          <div class="invoice-preview-total"><span>Amount due</span><span>${formatPrice(t.total)}</span></div>
        </div>

        ${memo ? `<div class="invoice-preview-memo"><div class="invoice-preview-label">Memo</div>${esc(memo)}</div>` : ''}

        <p class="text-secondary" style="font-size: var(--text-xs); margin-top: var(--space-4);">
          Stripe renders the final invoice with your account branding and a "Pay this invoice" link. Sending emails it to ${esc(email)} now; creating without sending lets you open it first and email it later from the list.
        </p>

        <div class="invoice-preview-actions">
          <button type="button" class="btn btn-sm" id="invoice-preview-edit">Back to edit</button>
          <div style="display:flex; gap: var(--space-2); flex-wrap:wrap;">
            <button type="button" class="btn btn-sm" id="invoice-preview-create">Create without sending</button>
            <button type="button" class="btn btn-primary btn-sm" id="invoice-preview-send">Create &amp; send to customer</button>
          </div>
        </div>
      </div>
    `
    document.body.appendChild(modal)

    const close = () => modal.remove()
    modal.addEventListener('click', e => { if (e.target === modal) close() })
    modal.querySelector('#invoice-preview-close').addEventListener('click', close)
    modal.querySelector('#invoice-preview-edit').addEventListener('click', close)
    const onKey = e => { if (e.key === 'Escape') { close(); document.removeEventListener('keydown', onKey) } }
    document.addEventListener('keydown', onKey)

    const act = async (sendEmail) => {
      const btns = modal.querySelectorAll('button')
      btns.forEach(b => { b.disabled = true })
      modal.querySelector(sendEmail ? '#invoice-preview-send' : '#invoice-preview-create').textContent = 'Creating…'
      await createInvoice(form, sendEmail)
      close()
    }
    modal.querySelector('#invoice-preview-create').addEventListener('click', () => act(false))
    modal.querySelector('#invoice-preview-send').addEventListener('click', () => act(true))
  }

  document.getElementById('billing-form').addEventListener('submit', async (e) => {
    e.preventDefault()
    hideBillingMessages()

    if (lines.length === 0) return showBillingError('Add at least one line item.')
    for (let i = 0; i < lines.length; i++) {
      if (!lines[i].description.trim()) return showBillingError(`Line ${i + 1}: description is required.`)
    }
    if (computeTotals().total < MIN_TOTAL_CENTS) return showBillingError('Total must be at least $0.50.')

    if (submitKind === 'invoice') showInvoicePreview(e.target)
    else await createPaymentLink(e.target)
  })

  wrap.addEventListener('click', (e) => {
    const copyBtn = e.target.closest('[data-copy]')
    if (copyBtn) {
      e.preventDefault()
      copyToClipboard(copyBtn.dataset.copy)
    }
  })

  renderLines()

  // Load catalog for the product picker (non-blocking), then start with one product line
  getAllProducts()
    .then(list => {
      products = (list || []).filter(p => p.price_cents > 0)
      if (lines.length === 0 && products.length) addProductLine()
    })
    .catch(err => showBillingError('Could not load products: ' + (err.message || 'unknown error')))

  loadBillingLists(email)
}

async function loadBillingLists(email) {
  const lists = document.getElementById('billing-lists')
  if (!lists) return

  try {
    const data = await billingRequest({ action: 'list', email })
    const invoices = data.invoices || []
    const links = data.payment_links || []

    lists.innerHTML = `
      <div style="display:grid; grid-template-columns: 1fr; gap: var(--space-6);">
        <div>
          <h4 style="font-size: var(--text-xs); text-transform:uppercase; letter-spacing:0.06em; color: var(--color-text-muted); margin-bottom: var(--space-2);">
            Invoices (${invoices.length})
          </h4>
          ${invoices.length === 0
            ? '<p class="text-sm text-secondary">No invoices yet.</p>'
            : `<div class="admin-table-wrap"><table class="admin-table">
                <thead><tr><th>Date</th><th>Invoice</th><th>Description</th><th>Amount</th><th>Status</th><th>Due</th><th style="width:1%;">Actions</th></tr></thead>
                <tbody>${invoices.map(renderInvoiceRow).join('')}</tbody>
              </table></div>`}
        </div>
        <div>
          <h4 style="font-size: var(--text-xs); text-transform:uppercase; letter-spacing:0.06em; color: var(--color-text-muted); margin-bottom: var(--space-2);">
            Payment links (${links.length})
          </h4>
          ${links.length === 0
            ? '<p class="text-sm text-secondary">No payment links yet.</p>'
            : `<div class="admin-table-wrap"><table class="admin-table">
                <thead><tr><th>Date</th><th>Description</th><th>Status</th><th>Payments</th><th style="width:1%;">Actions</th></tr></thead>
                <tbody>${links.map(renderPaymentLinkRow).join('')}</tbody>
              </table></div>`}
        </div>
      </div>
    `

    lists.querySelectorAll('[data-void]').forEach(btn => {
      btn.addEventListener('click', async () => {
        if (!confirm('Void this invoice? The customer will no longer be able to pay it.')) return
        btn.disabled = true
        try {
          await billingRequest({ action: 'void_invoice', email, id: btn.dataset.void })
          showToast('Invoice voided')
          await loadBillingLists(email)
        } catch (err) {
          showBillingError(err.message)
          btn.disabled = false
        }
      })
    })

    lists.querySelectorAll('[data-send]').forEach(btn => {
      btn.addEventListener('click', async () => {
        btn.disabled = true
        btn.textContent = 'Sending…'
        try {
          await billingRequest({ action: 'send_invoice', email, id: btn.dataset.send })
          showToast('Invoice emailed')
          await loadBillingLists(email)
        } catch (err) {
          showBillingError(err.message)
          btn.disabled = false
          btn.textContent = 'Email'
        }
      })
    })

    lists.querySelectorAll('[data-deactivate]').forEach(btn => {
      btn.addEventListener('click', async () => {
        if (!confirm('Deactivate this payment link? The URL will stop accepting payments.')) return
        btn.disabled = true
        try {
          await billingRequest({ action: 'deactivate_payment_link', email, id: btn.dataset.deactivate })
          showToast('Payment link deactivated')
          await loadBillingLists(email)
        } catch (err) {
          showBillingError(err.message)
          btn.disabled = false
        }
      })
    })
  } catch (err) {
    lists.innerHTML = `<div class="admin-error">${esc(err.message || 'Failed to load billing')}</div>`
  }
}

function renderInvoiceRow(inv) {
  const badge = {
    paid: 'admin-badge-active',
    open: 'admin-badge-warning',
    draft: 'admin-badge-inactive',
    void: 'admin-badge-inactive',
    uncollectible: 'admin-badge-inactive',
  }[inv.status] || 'admin-badge-inactive'
  const canAct = inv.status === 'open'
  return `
    <tr>
      <td style="white-space:nowrap;">${fmtDate(inv.created)}</td>
      <td class="cell-sku">${esc(inv.number || inv.id)}</td>
      <td class="cell-name" title="${esc(inv.description || '')}">${esc(inv.description || '—')}</td>
      <td class="font-semibold" style="white-space:nowrap;">${formatPrice(inv.total_cents)}</td>
      <td><span class="admin-badge ${badge}">${esc(inv.status)}</span></td>
      <td style="white-space:nowrap;">${inv.status === 'paid' ? `paid ${fmtDate(inv.paid_at)}` : (inv.due_date ? fmtDate(inv.due_date) : '—')}</td>
      <td class="cell-actions">
        ${inv.hosted_invoice_url ? `<a class="btn btn-sm" style="font-size:var(--text-xs);" href="${esc(inv.hosted_invoice_url)}" target="_blank" rel="noopener">Open</a>` : ''}
        ${inv.hosted_invoice_url ? `<button class="btn btn-sm" style="font-size:var(--text-xs);" data-copy="${esc(inv.hosted_invoice_url)}">Copy</button>` : ''}
        ${canAct ? `<button class="btn btn-sm" style="font-size:var(--text-xs);" data-send="${esc(inv.id)}">Email</button>` : ''}
        ${canAct ? `<button class="btn btn-sm" style="font-size:var(--text-xs); border-color: var(--color-error); color: var(--color-error);" data-void="${esc(inv.id)}">Void</button>` : ''}
      </td>
    </tr>
  `
}

function renderPaymentLinkRow(link) {
  const paid = link.paid_count > 0
  return `
    <tr>
      <td style="white-space:nowrap;">${fmtDate(link.created)}</td>
      <td class="cell-name" title="${esc(link.description || '')}">${esc(link.description || link.id)}</td>
      <td><span class="admin-badge ${link.active ? 'admin-badge-active' : 'admin-badge-inactive'}">${link.active ? 'active' : 'inactive'}</span></td>
      <td style="white-space:nowrap;">
        ${paid
          ? `<span class="admin-badge admin-badge-active">paid</span> ${link.paid_count}× · ${formatPrice(link.paid_total_cents)}${link.last_paid_at ? ` · ${fmtDate(link.last_paid_at)}` : ''}`
          : '<span class="text-secondary">none yet</span>'}
      </td>
      <td class="cell-actions">
        ${link.url ? `<a class="btn btn-sm" style="font-size:var(--text-xs);" href="${esc(link.url)}" target="_blank" rel="noopener">Open</a>` : ''}
        ${link.url ? `<button class="btn btn-sm" style="font-size:var(--text-xs);" data-copy="${esc(link.url)}">Copy</button>` : ''}
        ${link.active ? `<button class="btn btn-sm" style="font-size:var(--text-xs); border-color: var(--color-error); color: var(--color-error);" data-deactivate="${esc(link.id)}">Deactivate</button>` : ''}
      </td>
    </tr>
  `
}

/** Format a unit price that may be fractional cents (e.g. 3999.5 → $39.995). */
function fmtUnit(cents) {
  const rounded = Math.round(cents)
  if (Math.abs(cents - rounded) < 0.01) return formatPrice(rounded)
  return '$' + (cents / 100).toFixed(3)
}

function fmtDate(unixSeconds) {
  if (!unixSeconds) return '—'
  return new Date(unixSeconds * 1000).toLocaleDateString()
}

async function copyToClipboard(text, message = 'Link copied to clipboard') {
  try {
    await navigator.clipboard.writeText(text)
    showToast(message)
  } catch {
    showToast('Could not copy — use Open and copy the URL', 'error')
  }
}

function showBillingError(msg) {
  const el = document.getElementById('billing-error')
  if (!el) return
  el.innerHTML = esc(msg)
  el.style.display = ''
}

function showBillingSuccess(html) {
  const el = document.getElementById('billing-success')
  if (!el) return
  el.innerHTML = html
  el.style.display = ''
}

function hideBillingMessages() {
  for (const id of ['billing-error', 'billing-success']) {
    const el = document.getElementById(id)
    if (el) el.style.display = 'none'
  }
}

function esc(str) {
  return String(str || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
}
